import { LitElement, noChange } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import style from './InputGroup.light-dom.css'

export type InputGroupAddonPosition = 'inline-start' | 'inline-end' | 'block-start' | 'block-end'

const positions = new Set<InputGroupAddonPosition>(['inline-start', 'inline-end', 'block-start', 'block-end'])

/**
 * Positions reusable Nord content around the text control in an Input Group.
 * Its children retain their native semantics and interaction behavior.
 *
 * @status draft
 * @category form
 * @parent InputGroup
 * @lightDom true
 *
 * @slot - Text, icons, keyboard hints, spinners, buttons, dropdowns, or other reusable content.
 */
@customElement('nord-input-group-addon')
export default class InputGroupAddon extends DraftComponentMixin(LitElement) {
  private readonly contentObserver = new MutationObserver(() => this.updateCompositionState())

  /** Logical position around the grouped text control. */
  @property({
    reflect: true,
    converter: {
      fromAttribute: value => positions.has(value as InputGroupAddonPosition)
        ? value as InputGroupAddonPosition
        : 'inline-start',
      toAttribute: value => positions.has(value as InputGroupAddonPosition) ? value : 'inline-start',
    },
  })
  position: InputGroupAddonPosition = 'inline-start'

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (name === 'position' && newValue !== null && !positions.has(newValue as InputGroupAddonPosition)) {
      this.setAttribute('position', 'inline-start')
      return
    }
    super.attributeChangedCallback(name, oldValue, newValue)
  }

  connectedCallback() {
    super.connectedCallback()
    injectLightDomStyles(this, style, 'nord-input-group')
    this.contentObserver.observe(this, {
      attributeFilter: ['size'],
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    })
    this.updateCompositionState()
  }

  disconnectedCallback() {
    this.contentObserver.disconnect()
    super.disconnectedCallback()
  }

  protected willUpdate() {
    if (!positions.has(this.position)) {
      this.position = 'inline-start'
    }
  }

  protected createRenderRoot() {
    return this
  }

  protected render() {
    return noChange
  }

  private updateCompositionState() {
    const directElements = Array.from(this.children)
    const hasOnlyIgnorableText = Array.from(this.childNodes).every(node =>
      node.nodeType !== Node.TEXT_NODE || node.textContent?.trim() === '',
    )
    const isIconOnly = directElements.length === 1
      && directElements[0]?.localName === 'nord-icon'
      && hasOnlyIgnorableText
    const layoutElements = directElements.filter(element => element.localName !== 'nord-tooltip')
    const hasOnlyControlContent = directElements.every(element =>
      ['nord-button', 'nord-dropdown', 'nord-tooltip'].includes(element.localName),
    )
    const isControlOnly = layoutElements.length === 1
      && ['nord-button', 'nord-dropdown'].includes(layoutElements[0]?.localName ?? '')
      && hasOnlyControlContent
      && hasOnlyIgnorableText
    const matchedControl = layoutElements[0] as HTMLElement & { size?: string } | undefined
    const controlSize = isControlOnly
      ? (matchedControl?.size ?? matchedControl?.getAttribute('size') ?? 'm')
      : undefined

    this.toggleAttribute('data-n-input-group-icon-only', isIconOnly)
    this.toggleAttribute('data-n-input-group-control-only', isControlOnly)
    if (controlSize && ['s', 'm', 'l'].includes(controlSize)) {
      this.setAttribute('data-n-input-group-control-size', controlSize)
    }
    else {
      this.removeAttribute('data-n-input-group-control-size')
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-input-group-addon': InputGroupAddon
  }
}
